import { useEffect, useRef, useState, type JSX } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useAdminCronRuns, useTriggerCronRun } from '@/hooks/useAdmin';
import { useInvalidateMoney } from '@/hooks/useInvalidateMoney';
import { ApiError } from '@/api/client';
import type { CronRun } from '@/api/types';

/** The morning sweep: the same check as each row's "Re-check status", for every invite. */
export const RECHECK_JOB = 'outreach-morning';

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/** "Checked 312 · 14 changed · 3 now posted · 2 failed", from the job's summary. */
export function recheckSummaryText(run: Pick<CronRun, 'status' | 'summary' | 'errorMessage'>): string {
  if (run.status === 'FAILED') return `Recheck failed: ${run.errorMessage ?? 'unknown error'}`;
  const s = run.summary ?? {};
  const parts = [`Checked ${num(s.resolved).toLocaleString()}`, `${num(s.updated).toLocaleString()} changed`];
  if (num(s.converted) > 0) parts.push(`${num(s.converted).toLocaleString()} now posted`);
  if (num(s.followersUpdated) > 0) parts.push(`${num(s.followersUpdated).toLocaleString()} follower counts updated`);
  if (num(s.rejectedClipsAdjusted) > 0) parts.push(`${num(s.rejectedClipsAdjusted).toLocaleString()} rejected clips found`);
  if (num(s.failed) > 0) parts.push(`${num(s.failed).toLocaleString()} couldn't be checked`);
  return parts.join(' · ');
}

/**
 * Invites header: run the morning recheck for every invite now, instead of
 * one row at a time or waiting for tomorrow. Shows the run in progress —
 * including the scheduled one, or one another admin started — and refreshes
 * the list and owed totals when it finishes.
 */
export function RecheckAllButton(): JSX.Element {
  const qc = useQueryClient();
  const invalidateMoney = useInvalidateMoney();
  // Set when we start a run, so an older backend that doesn't report
  // `running` still shows progress until the finished run is recorded.
  const [startedHere, setStartedHere] = useState<string | null>(null);
  const runs = useAdminCronRuns({ perJob: 1, watchJob: RECHECK_JOB, watching: startedHere !== null });
  const trigger = useTriggerCronRun();

  const group = runs.data?.groups.find((g) => g.jobName === RECHECK_JOB);
  const last = group?.runs[0] ?? null;
  const finishedSinceStart = !!(startedHere && last && last.startedAt >= startedHere);
  const running = group?.running ?? (startedHere !== null && !finishedSinceStart);
  const runningSince = group?.runningSince ?? (running ? startedHere : null);

  // When a run we watched finishes: refresh what it changed and say what it did.
  const wasRunning = useRef(false);
  useEffect(() => {
    if (wasRunning.current && !running) {
      void qc.invalidateQueries({ queryKey: ['admin', 'invites'] });
      invalidateMoney();
      if (last) {
        const text = recheckSummaryText(last);
        if (last.status === 'FAILED') toast.error(text);
        else toast.success(`Recheck finished. ${text}`);
      }
      setStartedHere(null);
    }
    wasRunning.current = running;
  }, [running, last, qc, invalidateMoney]);

  function start(): void {
    trigger.mutate(RECHECK_JOB, {
      onSuccess: (res) => {
        setStartedHere(res.startedAt);
        toast.success("Rechecking every invite. You can keep working; the list refreshes when it's done.");
      },
      onError: (err: Error) => {
        if (err instanceof ApiError && err.status === 409) {
          setStartedHere(new Date().toISOString());
          toast.info('A recheck is already running. The list refreshes when it finishes.');
        } else {
          toast.error(`Couldn't start the recheck: ${err.message}`);
        }
      },
    });
  }

  if (running) {
    return (
      <Button type="button" variant="outline" size="sm" disabled aria-live="polite">
        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
        Rechecking…
        {runningSince && (
          <span className="ml-1 font-normal text-muted-foreground">
            started {formatDistanceToNow(new Date(runningSince), { addSuffix: true })}
          </span>
        )}
      </Button>
    );
  }

  const lastLine = last
    ? `Last full check ${formatDistanceToNow(new Date(last.startedAt), { addSuffix: true })}${last.status === 'FAILED' ? ' (failed)' : ''}.`
    : 'No full check recorded yet.';

  return (
    <ConfirmDialog
      trigger={
        <Button type="button" variant="outline" size="sm" disabled={trigger.isPending} title={lastLine}>
          {trigger.isPending ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
          )}
          Recheck all statuses
        </Button>
      }
      title="Recheck every invite now?"
      description={
        <>
          Runs the morning check now: asks clipping about every replied, joined and owed invite (joins, posts,
          followers) and updates what each is worth. It's the same as each row's Re-check status, for all of them.
          <span className="mt-2 block">
            It takes a few minutes. You can keep working; the list refreshes when it's done. {lastLine}
          </span>
        </>
      }
      confirmLabel="Recheck all"
      pending={trigger.isPending}
      onConfirm={start}
    />
  );
}
